import React from 'react';
import { FigureCard } from './FigureCard';
interface Stage {
  label: string;
  value: string;
  equation: string;
  size: number; // visual width fraction 0..1
  color: string;
  textColor: string;
}
const stages: Stage[] = [{
  label: 'Thermal Input',
  value: 'Q_in = 115,115 J',
  equation: 'Q = m·c·ΔT',
  size: 1.0,
  color: '#8B2500',
  textColor: '#fff'
}, {
  label: 'Steam Generation',
  value: '~0.1% as steam',
  equation: 'Q_steam = m·L_v',
  size: 0.62,
  color: '#C2410C',
  textColor: '#fff'
}, {
  label: 'Mechanical Rotation',
  value: 'Motor turns',
  equation: 'P_mech = F·v',
  size: 0.4,
  color: '#EF9F27',
  textColor: '#1B1B1B'
}, {
  label: 'EMF Generation',
  value: 'ε induced',
  equation: 'ε = −N·dΦ/dt',
  size: 0.24,
  color: '#60A5FA',
  textColor: '#1B1B1B'
}, {
  label: 'Electrical Output',
  value: 'E_out = 77.5 μJ',
  equation: 'P = V²/R',
  size: 0.1,
  color: '#1A4A68',
  textColor: '#fff'
}];
const losses = [{
  label: 'Incomplete vaporisation',
  detail: '99.9% remains as sensible heat in water',
  color: '#8B2500'
}, {
  label: 'Uninsulated tubing',
  detail: 'Steam cools ~5–10 °C en route to motor',
  color: '#C2410C'
}, {
  label: 'Motor friction',
  detail: 'Bearing & brush losses; sound + heat',
  color: '#EF9F27'
}, {
  label: 'Impedance mismatch',
  detail: '100 kΩ load reflects ~95% available power',
  color: '#60A5FA'
}];
export function Figure5() {
  return <FigureCard number={5} title="Energy Conversion Chain — From Thermal Input to Electrical Output" subtitle="Energy conversion cascade at T_hot = 80 °C. Each step shows a governing physical law and the dominant loss mechanism at that stage." caption="Waterfall representation of energy degradation. The visual width of each block is proportional to the (log-compressed) energy remaining at that stage — the final electrical output is a vanishingly small fraction of Q_in." data-id="element-244">
      <div className="bg-[#FBFAF6] border border-[#E5E0D6] rounded-sm p-6" data-id="element-245">
        {/* Formula chain */}
        <div className="mb-6 p-3 bg-white border border-[#1B1B1B]/20 rounded-sm" data-id="element-246">
          <div className="font-sans text-[9px] uppercase tracking-wider text-[#1B1B1B] font-semibold mb-1" data-id="element-247">
            Governing equation chain
          </div>
          <div className="font-mono text-[12px] text-[#1B1B1B] overflow-x-auto whitespace-nowrap" data-id="element-248">
            Q<sub data-id="element-249">in</sub> <span className="text-[#8B2500]" data-id="element-250">→</span> Q
            <sub data-id="element-251">steam</sub> <span className="text-[#C2410C]" data-id="element-252">→</span> W
            <sub data-id="element-253">mech</sub> <span className="text-[#EF9F27]" data-id="element-254">→</span> W
            <sub data-id="element-255">elec</sub> <span className="text-[#1A4A68]" data-id="element-256">=</span> E
            <sub data-id="element-257">out</sub>
            <span className="text-[#5B5448] italic ml-4" data-id="element-258">
              | Q = m·c·ΔT &nbsp; · &nbsp; P = F·v &nbsp; · &nbsp; ε = −N·dΦ/dt
              &nbsp; · &nbsp; P = V²/R
            </span>
          </div>
        </div>

        {/* Waterfall stages */}
        <div className="space-y-1" data-id="element-259">
          {stages.map((s, i) => {
          const widthPct = 35 + s.size * 60; // 35–95%
          const loss = losses[i];
          return <div key={s.label} data-id="element-260">
                <div className="flex items-center gap-3" data-id="element-261">
                  <div className="w-7 font-sans text-[11px] font-semibold text-[#5B5448] text-right" data-id="element-262">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="rounded-sm px-4 py-3 shadow-sm transition-all" style={{
                width: `${widthPct}%`,
                background: s.color,
                color: s.textColor
              }} data-id="element-263">
                    <div className="flex items-baseline justify-between gap-4" data-id="element-264">
                      <div data-id="element-265">
                        <div className="font-serif font-bold text-[14px] leading-tight" data-id="element-266">
                          {s.label}
                        </div>
                        <div className="font-mono text-[11px] opacity-90 mt-0.5" data-id="element-267">
                          {s.value}
                        </div>
                      </div>
                      <div className="font-mono text-[11px] opacity-80 whitespace-nowrap" data-id="element-268">
                        {s.equation}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loss arrow */}
                {loss && <div className="flex items-center gap-3 my-1.5" data-id="element-269">
                    <div className="w-7" data-id="element-270" />
                    <div className="flex items-center gap-2 pl-8" data-id="element-271">
                      <svg width="14" height="22" viewBox="0 0 14 22" data-id="element-272">
                        <line x1="7" y1="0" x2="7" y2="16" stroke={loss.color} strokeWidth="2" strokeDasharray="3 3" data-id="element-273" />
                        <polygon points="2,15 12,15 7,21" fill={loss.color} data-id="element-274" />
                      </svg>
                      <div className="text-[11px] font-sans" data-id="element-275">
                        <span className="font-semibold" style={{
                    color: loss.color
                  }} data-id="element-276">
                          Loss: {loss.label}
                        </span>
                        <span className="text-[#5B5448] italic ml-2" data-id="element-277">
                          — {loss.detail}
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>;
        })}
        </div>

        {/* Summary box */}
        <div className="mt-6 bg-[#FEF3C7] border-2 border-[#EF9F27] rounded-sm p-4 grid grid-cols-3 gap-4" data-id="element-278">
          <div data-id="element-279">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#B45309] font-semibold" data-id="element-280">
              Real efficiency
            </div>
            <div className="font-mono text-[14px] font-bold text-[#8B2500] mt-1" data-id="element-281">
              η_real = 6.73 × 10⁻⁸ %
            </div>
          </div>
          <div data-id="element-282">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#B45309] font-semibold" data-id="element-283">
              Theoretical max
            </div>
            <div className="font-mono text-[14px] font-bold text-[#1A4A68] mt-1" data-id="element-284">
              η_Carnot = 15.6 %
            </div>
          </div>
          <div data-id="element-285">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#B45309] font-semibold" data-id="element-286">
              Gap
            </div>
            <div className="font-mono text-[14px] font-bold text-[#1B1B1B] mt-1" data-id="element-287">
              ~ 8 orders of magnitude
            </div>
          </div>
        </div>
      </div>
    </FigureCard>;
}